import { PendingActionInterface } from '@app/core/interfaces';
import { Dossier } from '@app/core/interfaces/dossier';
import { ProcessStep } from '@app/core/interfaces/processStep';
import * as fromClaims from '../actions/claims.actions';
import * as fromClearActions from '../actions/clear.actions';

/**
 *  ids equals dossier_volgnummer
 */
export interface State {
    ids: string[];
    entities: { [id: string]: Dossier };
    processSteps: { [id: string]: ProcessStep };
    actions: { [id: string]: PendingActionInterface };
    attachments: any[] | null;
    selectedId: number | null;
    loading: boolean;
    loaded: boolean;
}

const initialState: State = {
    ids: [],
    entities: {},
    processSteps: {},
    actions: {},
    attachments: [],
    selectedId: null,
    loading: false,
    loaded: false,
};

/**
 *  Object.assign(target, ...sources);
 *
 *  Properties in the target object will be overwritten by properties in the sources if they have the same key.
 *  Later sources' properties will similarly overwrite earlier ones.
 *
 *  Object.assign({}, currentEntities, newEntities);
 */

function dossierReducer(_entities: { [id: string]: Dossier }, dossier: Dossier) {
    return Object.assign({}, _entities, {
        [dossier.dossier_volgnummer]: Object.assign({}, _entities[dossier.dossier_volgnummer] || {}, dossier),
    });
}

export function reducer(state = initialState, action: fromClaims.Actions): State {
    switch (action.type) {
        case fromClaims.LOAD: {
            const loaded = state.loaded;

            return {
                ...state,
                loading: true,
                loaded: loaded,
            };
        }

        case fromClaims.LOAD_SUCCESS: {
            // New entities
            const dossiers = action.payload;

            if (!dossiers) {
                return {
                    ...state,
                    loading: false,
                    loaded: true,
                };
            }

            // Create new ids
            const newDossierIds = dossiers.map((dossier) => dossier.dossier_volgnummer);

            // Return new state
            return {
                ...state,
                ids: [...state.ids, ...newDossierIds],
                entities: Object.assign({}, dossiers.reduce(dossierReducer, state.entities)),
                loading: false,
                loaded: true,
            };
        }

        case fromClaims.LOAD_DETAIL_SUCCESS: {
            // New entities
            const dossier = action.payload;

            if (!dossier) {
                return state;
            }

            // Copy new entity to old entities
            const entity = state.entities[dossier.dossier_volgnummer];
            const newDossierEntity = Object.assign({}, entity, dossier);

            // Return new state
            return {
                ...state,
                entities: Object.assign({}, state.entities, {
                    [dossier.dossier_volgnummer]: newDossierEntity,
                }),
                loading: false,
            };
        }

        case fromClaims.LOAD_PROCESS_STEPS_SUCCESS: {
            // New entities
            const processSteps = action.payload.processSteps;

            if (!processSteps) {
                return state;
            }

            // Copy new entitie to old entities
            const entity = state.processSteps[action.payload.dossier_volgnummer];
            const newProcessStepsEntity = Object.assign({}, entity, processSteps);

            // Return new state
            return {
                ...state,
                processSteps: Object.assign({}, state.processSteps, {
                    [action.payload.dossier_volgnummer]: newProcessStepsEntity,
                }),
            };
        }

        case fromClaims.LOAD_ACTIONS_SUCCESS: {
            // New entities
            const actions = action.payload.actions[0];

            if (!actions) {
                return state;
            }

            // Copy new entitie to old entities
            const entity = state.actions[action.payload.dossier_volgnummer];
            const newActionsEntity = Object.assign({}, entity, actions);

            // Return new state
            return {
                ...state,
                actions: Object.assign({}, state.actions, {
                    [action.payload.dossier_volgnummer]: newActionsEntity,
                }),
            };
        }

        case fromClaims.UPDATE_APPOINTMENT_ACTIONS: {
            const _action = state.actions[action.payload.dossier_volgnummer];
            const _entity = state.entities[action.payload.dossier_volgnummer];

            if (!_action || !_entity) {
                return state;
            }

            // Return new state
            return {
                ...state,
                entities: Object.assign({}, state.entities, {
                    [action.payload.dossier_volgnummer]: {
                        ..._entity,
                        aantal_acties: _entity.aantal_acties < 1 ? 0 : _entity.aantal_acties - 1
                    },
                }),
                actions: Object.assign({}, state.actions, {
                    [action.payload.dossier_volgnummer]: {
                        ..._action,
                        afspraken: null,
                        aantal_acties:  _action.aantal_acties < 1 ? 0 : _action.aantal_acties - 1
                    },
                }),
            };
        }

        case fromClaims.UPDATE_INVOLVED_PARTIES_ACTIONS: {
            const _action = { ...state.actions[action.payload.dossier_volgnummer] };
            const _entity = { ...state.entities[action.payload.dossier_volgnummer] };
            const _type = { ...action.payload.type };

            if (!_action || !_entity || !_type) {
                return state;
            }

            // If not includes
            const partijen = _action.partijen ? _action.partijen.map((partij) => partij.type) : [];

            if (partijen.includes(_type)) {
                // Change entity
                _entity.aantal_acties = _entity.aantal_acties < 1 ? 0 : _entity.aantal_acties - 1;

                // Change action
                _action.aantal_acties = _action.aantal_acties < 1 ? 0 : _action.aantal_acties - 1;
                _action.partijen = _action.partijen ? _action.partijen.filter((partij) => partij.type !== _type) : [];
            }

            // Return new state
            return {
                ...state,
                entities: Object.assign({}, state.entities, {
                    [action.payload.dossier_volgnummer]: _entity,
                }),
                actions: Object.assign({}, state.actions, {
                    [action.payload.dossier_volgnummer]: _action,
                }),
            };
        }

        case fromClaims.UPDATE_DOCUMENT_ACTIONS: {
            const _action = { ...state.actions[action.payload.dossier_volgnummer] };
            const _entity = { ...state.entities[action.payload.dossier_volgnummer] };

            if (!_action || !_entity) {
                return state;
            }

            const total_bijlagen = _action.bewijsstukken.bijlagen.length;

            // Change entity
            _entity.aantal_acties = _entity.aantal_acties < 1 ? 0 : _entity.aantal_acties - total_bijlagen;

            // Change action
            _action.bewijsstukken = null;
            _action.aantal_acties = _action.aantal_acties < 1 ? 0 : _action.aantal_acties - total_bijlagen;

            // Return new state
            return {
                ...state,
                entities: Object.assign({}, state.entities, {
                    [action.payload.dossier_volgnummer]: _entity,
                }),
                actions: Object.assign({}, state.actions, {
                    [action.payload.dossier_volgnummer]: _action,
                }),
            };
        }

        case fromClaims.SET_SELECTED: {
            // New entities
            const dossier_volgnummer = action.payload.dossier_volgnummer;

            if (!dossier_volgnummer) {
                return state;
            }

            // Return new state
            return {
                ...state,
                selectedId: dossier_volgnummer,
            };
        }

        case fromClaims.CANCEL_SUCCESS: {
            const dossier_volgnummer = action.payload.dossier_volgnummer;

            if (!dossier_volgnummer) {
                return state;
            }

            // Create new ids
            const ids = state.ids.filter((id) => id !== dossier_volgnummer);

            // Remove entitie
            const { [dossier_volgnummer]: undefined, ...newDossierEntities } = state.entities;

            // Return new state
            return {
                ...state,
                ids: ids,
                entities: newDossierEntities,
                loading: false,
            };
        }

        case fromClearActions.CLEAR_CLAIMS_STORE: {
            return initialState;
        }

        default: {
            return state;
        }
    }
}

export const getIds = (state: State) => state.ids;
export const getEntities = (state: State) => state.entities;
export const getProcessSteps = (state: State) => state.processSteps;
export const getActions = (state: State) => state.actions;
export const getSelectedId = (state: State) => state.selectedId;
export const isLoaded = (state: State) => state.loaded;
